import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Tag, 
  Search, 
  Trash2, 
  Plus,
  Pencil,
  X,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultOffer = {
  title: '',
  description: '',
  short_description: '',
  price: 0,
  original_price: 0,
  image: '',
  valid_until: '',
  inclusions: [],
  terms: '',
  is_active: true
};

export default function AdminOffers() {
  const { token } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState(defaultOffer);
  const [inclusionInput, setInclusionInput] = useState('');

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/offers/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data);
    } catch (error) {
      toast.error('Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        original_price: formData.original_price || null
      };

      if (editingOffer) {
        await axios.put(`${API_URL}/api/offers/${editingOffer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Offer updated');
      } else {
        await axios.post(`${API_URL}/api/offers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Offer created');
      }
      
      setIsDialogOpen(false);
      setEditingOffer(null);
      setFormData(defaultOffer);
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      description: offer.description,
      short_description: offer.short_description,
      price: offer.price,
      original_price: offer.original_price || 0,
      image: offer.image,
      valid_until: offer.valid_until || '',
      inclusions: offer.inclusions,
      terms: offer.terms || '',
      is_active: offer.is_active
    });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (offer) => {
    try {
      await axios.put(`${API_URL}/api/offers/${offer.id}`, 
        { is_active: !offer.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Offer ${!offer.is_active ? 'activated' : 'deactivated'}`);
      fetchOffers();
    } catch (error) {
      toast.error('Failed to update offer');
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Offer deleted');
      fetchOffers();
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  const addInclusion = () => {
    if (inclusionInput.trim()) {
      setFormData({
        ...formData,
        inclusions: [...formData.inclusions, inclusionInput.trim()]
      });
      setInclusionInput('');
    }
  };

  const removeInclusion = (index) => {
    setFormData({
      ...formData,
      inclusions: formData.inclusions.filter((_, i) => i !== index)
    });
  };

  const filteredOffers = offers.filter(offer =>
    offer.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-offers-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Special Offers</h1>
          <p className="text-muted-foreground mt-1">{offers.length} offers</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingOffer(null);
            setFormData(defaultOffer);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-emerald" data-testid="add-offer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Offer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOffer ? 'Edit Offer' : 'Add New Offer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Short Description</Label>
                <Input
                  value={formData.short_description}
                  onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Full Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (₹)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>
                <div>
                  <Label>Original Price (₹, optional)</Label>
                  <Input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({...formData, original_price: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    placeholder="December 31, 2026"
                  />
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({...formData, image: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Inclusions</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={inclusionInput}
                    onChange={(e) => setInclusionInput(e.target.value)}
                    placeholder="Add inclusion"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInclusion())}
                  />
                  <Button type="button" variant="outline" onClick={addInclusion}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.inclusions.map((inclusion, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {inclusion}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeInclusion(idx)} />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Terms & Conditions</Label>
                <Textarea
                  value={formData.terms}
                  onChange={(e) => setFormData({...formData, terms: e.target.value})}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-emerald">
                  {editingOffer ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search offers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid gap-4">
        {filteredOffers.map((offer, index) => (
          <motion.div
            key={offer.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
          >
            <Card className={!offer.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-40 h-32 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={offer.image} alt={offer.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-serif text-lg text-foreground">{offer.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{offer.short_description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-xl text-foreground">₹{offer.price.toLocaleString()}</p>
                        {offer.original_price > 0 && (
                          <p className="text-sm text-muted-foreground line-through">₹{offer.original_price.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {offer.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                      {offer.valid_until && (
                        <Badge variant="outline">Valid until: {offer.valid_until}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => handleToggleActive(offer)}>
                        {offer.is_active ? (
                          <><XCircle className="w-4 h-4 mr-1" /> Deactivate</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                        )}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(offer)}>
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(offer.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {filteredOffers.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No offers found</p>
          </div>
        )}
      </div>
    </div>
  );
}
